import KitText from '@omni/kit/components/KitText';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import {
  PropsWithChildren,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import React, { ReactElement } from 'react';
import { Animated, StyleSheet } from 'react-native';
import Popup from 'reactjs-popup';

import { TooltipProps } from './index';

export default function Tooltip({
  label = 'Toolip',
  children,
  contentStyle = {},
  clearStyles = false,
  type = 'short',
  position = 'top center',
  ...props
}: PropsWithChildren<TooltipProps>): ReactElement {
  // Check if we're in fullscreen mode
  const isInFullscreenMode = useMemo(() => {
    return () =>
      Boolean(
        document.fullscreenElement ||
          (document as any).webkitFullscreenElement ||
          (document as any).mozFullScreenElement ||
          (document as any).msFullscreenElement
      );
  }, []);

  /**
   * When KitPlayer enters in fullscreen mode, in case popup-root is not inside
   * the player container, the Tooltip will not be displayed go "missing"
   * for KitPlayer buttons.
   *
   * This function moves the popup-root to the player container and there is no need
   * to manually remove the node from the original parent.
   * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Node/appendChild#description
   */
  const movePopupRoot = useCallback(() => {
    // Check if both popup-root and kit-player-container exist
    const popupRoot = document.getElementById('popup-root');
    const playerContainer = document.getElementById('kit-player-container');

    // Only proceed if both elements exist
    if (popupRoot && playerContainer) {
      // Check if we're in fullscreen mode
      const fullscreenMode = isInFullscreenMode();

      if (fullscreenMode) {
        if (popupRoot.parentElement !== playerContainer) {
          // Move popup-root inside player container
          playerContainer.appendChild(popupRoot);
        }
      } else {
        if (popupRoot.parentElement !== document.body) {
          // Move popup-root back to document.body
          document.body.appendChild(popupRoot);
        }
      }
    }
  }, [isInFullscreenMode]);
  const anim = { duration: 150, useNativeDriver: true };
  const values = { opacity: { init: 0, end: 1 }, scale: { init: 0.8, end: 1 } };

  const opacity = useRef(new Animated.Value(values.opacity.init)).current;
  const scale = useRef(new Animated.Value(values.scale.init)).current;

  const fadeIn = () => {
    Animated.timing(opacity, { ...anim, toValue: values.opacity.end }).start();
    Animated.timing(scale, { ...anim, toValue: values.scale.end }).start();
  };

  const fadeOut = () => {
    opacity.setValue(values.opacity.init);
    scale.setValue(values.scale.init);
  };

  useLayoutEffect(() => {
    const popupRoot = document.getElementById('popup-root');
    const playerContainer = document.getElementById('kit-player-container');

    // Proceed to add event in fullscreen, only if both elements exist
    if (popupRoot && playerContainer) {
      document.addEventListener('fullscreenchange', movePopupRoot);
      document.addEventListener('webkitfullscreenchange', movePopupRoot);
      document.addEventListener('mozfullscreenchange', movePopupRoot);
      document.addEventListener('MSFullscreenChange', movePopupRoot);

      return () => {
        document.removeEventListener('fullscreenchange', movePopupRoot);
        document.removeEventListener('webkitfullscreenchange', movePopupRoot);
        document.removeEventListener('mozfullscreenchange', movePopupRoot);
        document.removeEventListener('MSFullscreenChange', movePopupRoot);
      };
    }

    return undefined;
  }, [movePopupRoot]);

  return (
    <Popup
      on='hover'
      onOpen={fadeIn}
      onClose={fadeOut}
      trigger={
        <div
          className='button'
          role='button'
          style={{
            borderWidth: 0,
            backgroundColor: 'transparent',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
          }}
        >
          {children}
        </div>
      }
      position={position}
      arrowStyle={{ display: 'none' }}
      contentStyle={{ zIndex: 2 }}
      {...props}
    >
      <Animated.View
        style={[
          !clearStyles ? styles.tooltip : {},
          type === 'long' && {
            borderRadius: BorderRadius.l,
            maxWidth: 320,
            padding: Spacing.l,
          },
          contentStyle,
          { opacity, transform: [{ scale }] },
        ]}
      >
        <KitText
          white
          fontSize={14}
          fontWeight={600}
          style={type === 'long' ? { lineHeight: 20 } : null}
        >
          {label}
        </KitText>
      </Animated.View>
    </Popup>
  );
}

const styles = StyleSheet.create({
  tooltip: {
    borderRadius: 60,
    paddingVertical: Spacing.xs,
    paddingHorizontal: Spacing.m,
    backgroundColor: Colors.N1000,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
